import { useEffect, useRef } from 'react'
import { useRouter } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import { useCan } from '../../contexts/PermissionsContext'
import { useLiveSubscription } from '../../contexts/LiveEventsContext'
import { useBrowserNotifications } from '../../hooks/useBrowserNotifications'
import { diffUnreadRows, firstMeaningfulLine, showBrowserNotification } from '../../lib/browser-notifications'
import { threadTitleT } from './thread'
import type { Conversation } from '../../api/schema'

const WINDOW = 25
const TAG = 'conversations-new'

export function ConversationNotificationsWatcher({ org }: { org: string }) {
    const { t } = useTranslation('conversations')
    const router = useRouter()
    const canRead = useCan('conversations.read')
    const { enabled } = useBrowserNotifications()
    const active = canRead && enabled

    useLiveSubscription({ entity: 'conversations' }, { enabled: active })

    const list = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/conversations',
        { params: { path: { orgCode: org }, query: { limit: WINDOW } } },
        { enabled: active },
    )

    const baselineRef = useRef(new Map<string, number>())
    const seededRef = useRef(false)
    const activeAtRef = useRef(0)

    useEffect(() => {
        baselineRef.current = new Map()
        seededRef.current = false
        activeAtRef.current = active ? Date.now() : 0
    }, [active, org])

    useEffect(() => {
        if (!active || !list.data) return
        if (list.dataUpdatedAt < activeAtRef.current) return

        const rows = (list.data.data ?? []) as Conversation[]
        const { arrivals, baseline } = diffUnreadRows(baselineRef.current, rows, seededRef.current)
        baselineRef.current = baseline
        seededRef.current = true
        if (arrivals.length === 0) return

        const first = arrivals[0]

        showBrowserNotification({
            title:
                arrivals.length === 1
                    ? t('notifications.manager_one', { title: threadTitleT(first, t) })
                    : t('notifications.manager_many', { count: arrivals.length }),
            body:
                arrivals.length === 1 ? firstMeaningfulLine(first.preview || first.channel_identity) : '',
            tag: TAG,
            onClick: () => {
                void router.navigate({
                    to: '/$org/conversations',
                    params: { org },
                    search: {
                        conversation: arrivals.length === 1 ? first.uid : undefined,
                        status: undefined,
                        channel: undefined,
                        resolution: undefined,
                        asset: undefined,
                        unattached: undefined,
                        draft: undefined,
                        q: undefined,
                        selected: undefined,
                        reply_for_issue: undefined,
                        message: undefined,
                    },
                })
            },
        })
    }, [active, list.data, list.dataUpdatedAt, org, router, t])

    return null
}
